import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import {
  fetchExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  uploadReceipt,
  extractReceipt,
  newExpenseId,
} from '../lib/expensesApi.js';
import { compressForUpload } from '../lib/imageCompression.js';

// Cost splitting (Phase 9 mini-phase). Settlement groups live on
// trip.json. UI shows: running totals per group, full expense list,
// add-manual + add-via-receipt forms, per-row edit/delete.

const SPLIT_TYPES = [
  { id: 'even', label: 'Even (per person, weighted by group size)' },
  { id: 'payer_only', label: "Payer's group only (treat)" },
  { id: 'custom', label: 'Custom % per group' },
];

const TIP_PRESETS = [0, 15, 18, 20, 22, 25];

// Category options for new/edited expenses. Drives the tip-selector
// default — dining defaults to 18%, every other category defaults to
// 0% (the tip section becomes a small "+ add tip" toggle for the
// generous-rideshare-tipper edge case).
const EXPENSE_CATEGORIES = [
  { id: 'dining', label: 'Dining' },
  { id: 'groceries', label: 'Groceries' },
  { id: 'lodging', label: 'Lodging' },
  { id: 'transport', label: 'Transport · car / fuel / transit' },
  { id: 'activity', label: 'Activity · excursion / ticket' },
  { id: 'other', label: 'Other' },
];

function defaultTipPctForCategory(category) {
  return category === 'dining' ? 18 : 0;
}

export default function Costs() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const isSpectator = session?.role === 'spectator-judge';

  const refresh = useCallback(async () => {
    try {
      const res = await fetchExpenses({ tripId });
      setData(res);
      setError(null);
    } catch (err) {
      if (err.status === 'api_unavailable') {
        setData({ expenses: [], settlement_groups: [], settlement: { totals: {}, transfers: [] }, players: [] });
      } else if (err.status !== 401) {
        setError(err.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const groups = data?.settlement_groups || [];
  // Empty arrays are truthy in JS, so a literal `data?.players || fallback`
  // would skip the trip-config fallback whenever the server returns []
  // (which happens when readTrip fails or the GET 401s before data loads).
  // Always take the longer of the two to be safe.
  const apiPlayers = data?.players;
  const tripPlayers =
    trip?.players?.map((p) => ({ id: p.id, name: p.display_name || p.name })) || [];
  const players = apiPlayers && apiPlayers.length > 0 ? apiPlayers : tripPlayers;
  const expenses = data?.expenses || [];

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          cost splitting · live
        </p>
        <h1 className="mt-1 font-serif text-2xl">Costs</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Add an expense as you go; settlement updates live. Receipts get OCR&apos;d via Claude vision so you can confirm the numbers.
        </p>
      </header>

      {!session.loading && !meId && !isSpectator && (
        <div className="card border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            sign-in required
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open from your briefing-email link to add or edit expenses.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}

      <SettlementSummary
        groups={groups}
        settlement={data?.settlement}
        currency={expenses[0]?.currency || 'USD'}
      />

      {meId && !showAdd && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="w-full rounded-md border border-action bg-action px-3 py-2 font-mono text-xs uppercase tracking-wider text-action-fg"
        >
          + add expense
        </button>
      )}

      {meId && showAdd && (
        <ExpenseForm
          tripId={tripId}
          groups={groups}
          players={players}
          mePlayerId={meId}
          onCancel={() => setShowAdd(false)}
          onSaved={async () => {
            setShowAdd(false);
            await refresh();
          }}
        />
      )}

      <section aria-labelledby="expense-list" className="space-y-2">
        <h2
          id="expense-list"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-secondary"
        >
          expenses · {expenses.length}
        </h2>
        {loading && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            …loading
          </p>
        )}
        {expenses.length === 0 && !loading && (
          <div className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
            Nothing logged yet.
          </div>
        )}
        <ul className="space-y-2">
          {expenses.map((e) => (
            <ExpenseRow
              key={e.id}
              expense={e}
              groups={groups}
              players={players}
              meId={meId}
              isEditing={editingId === e.id}
              onEdit={() => setEditingId(e.id)}
              onCancelEdit={() => setEditingId(null)}
              onSaved={async () => {
                setEditingId(null);
                await refresh();
              }}
              onDelete={async () => {
                if (!window.confirm('Remove this expense?')) return;
                try {
                  await deleteExpense({ tripId, expenseId: e.id });
                  await refresh();
                } catch (err) {
                  setError(err.message || String(err));
                }
              }}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function SettlementSummary({ groups, settlement, currency }) {
  const totals = settlement?.totals || {};
  const transfers = settlement?.transfers || [];
  if (!groups || groups.length === 0) return null;

  return (
    <section
      aria-labelledby="settlement"
      className="card overflow-hidden"
    >
      <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-2">
        <h2
          id="settlement"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
        >
          settlement · running
        </h2>
      </header>
      <ul className="divide-y divide-border-tertiary">
        {groups.map((g) => {
          const t = totals[g.id] || { paid: 0, share: 0, net: 0 };
          const positive = t.net > 0;
          const tone = Math.abs(t.net) < 0.005
            ? 'text-fg-tertiary'
            : positive
              ? 'text-success'
              : 'text-danger';
          return (
            <li key={g.id} className="flex items-baseline justify-between gap-3 px-4 py-2">
              <div>
                <p className="font-medium">{g.label}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  paid {fmt(t.paid, currency)} · share {fmt(t.share, currency)}
                </p>
              </div>
              <p className={`font-mono text-sm ${tone}`}>
                {positive ? '+' : ''}
                {fmt(t.net, currency)}
              </p>
            </li>
          );
        })}
      </ul>
      {transfers.length > 0 && (
        <div className="border-t border-border-tertiary px-4 py-2 space-y-1">
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            settle up
          </p>
          {transfers.map((t, i) => {
            const from = groups.find((g) => g.id === t.from)?.label || t.from;
            const to = groups.find((g) => g.id === t.to)?.label || t.to;
            return (
              <p key={i} className="text-sm">
                <span className="text-danger">{from}</span> owes{' '}
                <span className="text-success">{to}</span>{' '}
                <span className="font-mono">{fmt(t.amount, currency)}</span>
              </p>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ExpenseRow({ expense, groups, players, meId, isEditing, onEdit, onCancelEdit, onSaved, onDelete }) {
  const payerName =
    players.find((p) => p.id === expense.paid_by)?.name || expense.paid_by;
  const canEdit = meId && (meId === expense.created_by || meId === expense.paid_by);
  const splitLabel =
    expense.split_type === 'even'
      ? 'even'
      : expense.split_type === 'payer_only'
        ? "payer's group"
        : 'custom %';

  if (isEditing) {
    return (
      <li className="card overflow-hidden">
        <ExpenseForm
          tripId={expense.trip_id}
          existing={expense}
          groups={groups}
          players={players}
          mePlayerId={meId}
          onCancel={onCancelEdit}
          onSaved={onSaved}
        />
      </li>
    );
  }

  return (
    <li className="card overflow-hidden">
      <div className="flex items-baseline justify-between gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-tight">{expense.what}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {payerName} paid · {splitLabel} ·{' '}
            {expense.occurred_at ? formatShortDate(expense.occurred_at) : ''}
          </p>
        </div>
        <p className="font-mono text-sm">{fmt(expense.total, expense.currency)}</p>
      </div>
      {expense.receipt_photo_url && (
        <img
          src={expense.receipt_photo_url}
          alt="receipt"
          className="max-h-40 w-full border-t border-border-tertiary object-contain bg-black"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      )}
      {(expense.subtotal != null || expense.tax != null || expense.tip != null || expense.notes) && (
        <div className="space-y-0.5 border-t border-border-tertiary px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {expense.subtotal != null && (
            <p>subtotal · <span className="normal-case tracking-normal text-fg-secondary">{fmt(expense.subtotal, expense.currency)}</span></p>
          )}
          {expense.tax != null && (
            <p>tax · <span className="normal-case tracking-normal text-fg-secondary">{fmt(expense.tax, expense.currency)}</span></p>
          )}
          {expense.tip != null && (
            <p>
              tip{expense.tip_included ? ' (included on receipt)' : ''} ·{' '}
              <span className="normal-case tracking-normal text-fg-secondary">{fmt(expense.tip, expense.currency)}</span>
            </p>
          )}
          {expense.notes && (
            <p className="normal-case tracking-normal italic text-fg-secondary">{expense.notes}</p>
          )}
        </div>
      )}
      {canEdit && (
        <div className="flex gap-2 border-t border-border-tertiary bg-bg-secondary px-3 py-1.5">
          <button
            type="button"
            onClick={onEdit}
            className="font-mono text-[10px] uppercase tracking-wider text-action underline-offset-4 hover:underline"
          >
            edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="font-mono text-[10px] uppercase tracking-wider text-danger underline-offset-4 hover:underline"
          >
            delete
          </button>
        </div>
      )}
    </li>
  );
}

function ExpenseForm({ tripId, existing, groups, players, mePlayerId, onCancel, onSaved }) {
  const [what, setWhat] = useState(existing?.what || '');
  const [paidBy, setPaidBy] = useState(existing?.paid_by || mePlayerId || '');
  const [splitType, setSplitType] = useState(existing?.split_type || 'even');
  const [splitCustom, setSplitCustom] = useState(() =>
    existing?.split_custom ||
    Object.fromEntries(groups.map((g) => [g.id, Math.round(100 / Math.max(groups.length, 1))]))
  );
  const [subtotal, setSubtotal] = useState(existing?.subtotal != null ? String(existing.subtotal) : '');
  const [tax, setTax] = useState(existing?.tax != null ? String(existing.tax) : '');
  const [tip, setTip] = useState(existing?.tip != null ? String(existing.tip) : '');
  const [tipIncluded, setTipIncluded] = useState(!!existing?.tip_included);
  const [category, setCategory] = useState(existing?.category || 'dining');
  const [tipPct, setTipPct] = useState(() =>
    defaultTipPctForCategory(existing?.category || 'dining')
  );
  // Show the inline tip-preset row by default for dining (current behaviour).
  // For other categories, hide the row behind a "+ add tip" toggle.
  const [tipPanelOpen, setTipPanelOpen] = useState(
    () => (existing?.category || 'dining') === 'dining' || !!existing?.tip
  );
  const [notes, setNotes] = useState(existing?.notes || '');
  const [receiptUrl, setReceiptUrl] = useState(existing?.receipt_photo_url || null);
  const [extracted, setExtracted] = useState(existing?.receipt_extracted || null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const total = useMemo(() => {
    const s = num(subtotal);
    const t = num(tax);
    const ti = num(tip);
    const sum = (s || 0) + (t || 0) + (ti || 0);
    return sum > 0 ? +sum.toFixed(2) : null;
  }, [subtotal, tax, tip]);

  const recomputeTip = useCallback(
    (pct) => {
      const s = num(subtotal);
      if (!s) return;
      const t = +(s * (pct / 100)).toFixed(2);
      setTip(String(t));
    },
    [subtotal]
  );

  async function pickReceipt(rawFile) {
    if (!rawFile) return;
    setUploading(true);
    setError(null);
    try {
      // Compress before upload — receipts photographed in-restaurant are
      // typically < 5 MB but cover this path for parity with the rest of
      // the flow. EXIF preserved (rarely useful for receipts but cheap).
      const file = await compressForUpload(rawFile);
      const expenseId = existing?.id || newExpenseId();
      const { secure_url } = await uploadReceipt({
        tripId,
        expenseId,
        file,
        onProgress: (p) => setProgress(p),
      });
      setReceiptUrl(secure_url);
      setUploading(false);
      setExtracting(true);
      const { extracted: ex } = await extractReceipt({ tripId, photoUrl: secure_url });
      setExtracted(ex);
      if (ex.business_name && !what) setWhat(ex.business_name);
      if (ex.subtotal != null) setSubtotal(String(ex.subtotal));
      if (ex.tax != null) setTax(String(ex.tax));
      if (ex.gratuity != null) setTip(String(ex.gratuity));
      if (ex.tip_included != null) setTipIncluded(ex.tip_included);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setUploading(false);
      setExtracting(false);
    }
  }

  // Custom split must sum to exactly 100. The server-side math will
  // auto-normalize otherwise, but we'd rather block + surface a clear
  // error so the user fixes the data instead of getting silently-
  // rescaled numbers in the settlement.
  const customSum = useMemo(() => {
    if (splitType !== 'custom') return 100;
    return groups.reduce((s, g) => s + (Number(splitCustom[g.id]) || 0), 0);
  }, [splitType, splitCustom, groups]);
  const customSumValid = splitType !== 'custom' || Math.abs(customSum - 100) < 0.5;
  const canSave = !!what.trim() && !!paidBy && total != null && customSumValid;

  async function save() {
    if (!what.trim() || !paidBy || total == null) {
      setError('What, payer, and total are required.');
      return;
    }
    if (!customSumValid) {
      setError(`Custom percentages must sum to 100 (currently ${customSum}).`);
      return;
    }
    setSaving(true);
    setError(null);
    const fields = {
      what: what.trim(),
      category,
      total,
      subtotal: num(subtotal),
      tax: num(tax),
      tip: num(tip),
      tip_included: tipIncluded,
      paid_by: paidBy,
      split_type: splitType,
      split_custom: splitType === 'custom' ? splitCustom : null,
      receipt_photo_url: receiptUrl,
      receipt_extracted: extracted,
      notes: notes || undefined,
    };
    try {
      if (existing) {
        await updateExpense({ tripId, expenseId: existing.id, ...fields });
      } else {
        await createExpense({ tripId, ...fields });
      }
      onSaved();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-3 p-3">
      {!existing && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          add expense
        </p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => pickReceipt(e.target.files?.[0])}
      />
      {!receiptUrl ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-md border border-dashed border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
        >
          📷 upload receipt (auto-fills via OCR)
        </button>
      ) : (
        <div className="space-y-1">
          <img
            src={receiptUrl}
            alt="receipt"
            className="max-h-32 w-full rounded-md border border-border-tertiary bg-black object-contain"
          />
          {extracted && (
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              OCR confidence · biz {pct(extracted.confidence?.business_name)} · total{' '}
              {pct(extracted.confidence?.total)} · tax {pct(extracted.confidence?.tax)}
            </p>
          )}
        </div>
      )}
      {(uploading || extracting) && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {uploading ? `uploading… ${Math.round(progress * 100)}%` : 'reading receipt…'}
        </p>
      )}

      <Field label="what">
        <input
          type="text"
          value={what}
          onChange={(e) => setWhat(e.target.value)}
          placeholder="Tracy's dinner / rental car / Airbnb"
          className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 text-sm"
        />
      </Field>

      <Field label="category">
        <select
          value={category}
          onChange={(e) => {
            const next = e.target.value;
            setCategory(next);
            const nextDefault = defaultTipPctForCategory(next);
            setTipPct(nextDefault);
            // Re-open the tip row for dining; collapse for others (unless
            // user already typed a tip manually).
            setTipPanelOpen(next === 'dining' || !!num(tip));
            // If they switched away from dining and tip was the default
            // 18%, reset it to 0 (don't accidentally tip the Airbnb host).
            if (next !== 'dining' && !num(tip)) setTip('');
          }}
          className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 text-sm"
        >
          {EXPENSE_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid grid-cols-3 gap-2">
        <Field label="subtotal">
          <input
            type="text"
            inputMode="decimal"
            value={subtotal}
            onChange={(e) => setSubtotal(e.target.value)}
            className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 font-mono text-sm"
          />
        </Field>
        <Field label="tax">
          <input
            type="text"
            inputMode="decimal"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 font-mono text-sm"
          />
        </Field>
        <Field label="tip">
          <input
            type="text"
            inputMode="decimal"
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 font-mono text-sm"
          />
        </Field>
      </div>

      {tipIncluded ? (
        <p className="rounded-md border border-copper/40 bg-copper/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-copper-dark">
          gratuity already included on receipt — don&apos;t double-tip
        </p>
      ) : tipPanelOpen ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            quick tip
          </span>
          {TIP_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setTipPct(p);
                recomputeTip(p);
                if (p === 0) setTip('');
              }}
              className={[
                'rounded-full border px-2 py-0.5 font-mono text-[10px] transition',
                tipPct === p
                  ? 'border-action bg-action text-action-fg'
                  : 'border-border-tertiary bg-bg-secondary text-fg-secondary',
              ].join(' ')}
            >
              {p === 0 ? 'No tip' : `${p}%`}
            </button>
          ))}
          {category !== 'dining' && (
            <button
              type="button"
              onClick={() => {
                setTipPanelOpen(false);
                setTipPct(0);
                setTip('');
              }}
              className="ml-auto font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-2 hover:underline"
            >
              hide
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setTipPanelOpen(true);
            setTipPct(15);
          }}
          className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-2 hover:underline"
        >
          + add tip
        </button>
      )}

      <label className="flex items-center gap-2 text-xs text-fg-secondary">
        <input
          type="checkbox"
          checked={tipIncluded}
          onChange={(e) => setTipIncluded(e.target.checked)}
        />
        Gratuity is already on the receipt
      </label>

      <Field label="paid by">
        <select
          value={paidBy}
          onChange={(e) => setPaidBy(e.target.value)}
          className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 text-sm"
        >
          <option value="">—</option>
          {players.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="split">
        <select
          value={splitType}
          onChange={(e) => setSplitType(e.target.value)}
          className="w-full rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 text-sm"
        >
          {SPLIT_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>

      {splitType === 'custom' && (
        <div className="space-y-1">
          {groups.map((g) => (
            <label key={g.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="font-mono uppercase tracking-wider text-fg-tertiary">
                {g.label}
              </span>
              <input
                type="number"
                min={0}
                max={100}
                value={splitCustom[g.id] ?? 0}
                onChange={(e) =>
                  setSplitCustom((prev) => ({ ...prev, [g.id]: Number(e.target.value) || 0 }))
                }
                className="w-16 rounded-md border border-border-tertiary bg-bg-primary px-2 py-1 font-mono text-sm"
              />
              <span className="text-fg-tertiary">%</span>
            </label>
          ))}
          <p
            className={`font-mono text-[10px] uppercase tracking-wider ${
              customSumValid ? 'text-fg-tertiary' : 'text-danger'
            }`}
          >
            sum · {customSum}%{customSumValid ? '' : ' · must equal 100 to save'}
          </p>
        </div>
      )}

      <Field label="notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full resize-y rounded-md border border-border-tertiary bg-bg-primary px-2 py-1.5 text-sm"
          maxLength={500}
        />
      </Field>

      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        total · <span className="normal-case tracking-normal text-fg-primary">{total != null ? fmt(total, 'USD') : '—'}</span>
      </p>

      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-2 py-1 font-mono text-[11px] text-danger">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || uploading || extracting || !canSave}
          className="flex-1 rounded-md border border-action bg-action px-3 py-2 font-mono text-xs uppercase tracking-wider text-action-fg disabled:opacity-50"
        >
          {saving ? 'saving…' : existing ? 'save' : 'add'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2 font-mono text-xs uppercase tracking-wider text-fg-secondary"
        >
          cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {label}
      </span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function pct(v) {
  if (v == null) return '—';
  return `${Math.round(v * 100)}%`;
}
function fmt(amount, currency = 'USD') {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
}
function formatShortDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
